import { Injectable } from '@angular/core'
import { Observable, Subject } from 'rxjs'
import { ApiService } from '@app/core/graphql/services/api.service'
import { PassUploadJob } from '@app/models/pass-upload-job.model'
import {
  GET_PASS_UPLOAD_JOBS,
  GET_PASS_UPLOAD_JOB,
  GET_PASS_UPLOAD_JOB_DEFECTS,
  GET_PASS_UPLOAD_JOB_ERROR_FILE,
  DISMISS_PASS_UPLOAD_JOB,
  CANCEL_PASS_UPLOAD_JOB,
} from '@app/query/pass-upload-job.query'

/** One errored row of an upload, as reported by get_pass_upload_job_defects. */
export interface PassUploadDefect {
  /** Excel row number in the uploaded file (header is row 1, first data row is 2). */
  row: number
  reason: string
  /** Best-effort identity (name / phone / email) pulled from the errored row. */
  identity: string
}

/** Envelope PAPI wraps every pass-upload operation in. */
export interface PassUploadResponse {
  success: boolean
  errorText?: string
}

/** A job straight off the wire — a plain object, not yet a PassUploadJob instance. */
export type RawPassUploadJob = Partial<PassUploadJob>

export interface PassUploadJobsResponse extends PassUploadResponse {
  data?: RawPassUploadJob[]
}

export interface PassUploadJobResponse extends PassUploadResponse {
  data?: RawPassUploadJob
}

export interface PassUploadDefectsResponse extends PassUploadResponse {
  total?: number
  defects?: PassUploadDefect[]
}

export interface PassUploadErrorFileResponse extends PassUploadResponse {
  /** base64 xlsx payload. */
  file?: string
  filename?: string
}

/**
 * API wrapper + tracking event bus for staged pass uploads (YB-1669).
 *
 * Jobs are queued by PAPI immediately after validation (no confirm gate) — any save
 * flow that receives a `pass_upload_job_id` on its event mutation response calls
 * `trackJob(jobId)`. The UploadTasksService listens on `trackRequests$`, follows the
 * job's progress in the navbar bell panel, and opens the errors dialog if the file
 * had invalid rows.
 */
@Injectable({ providedIn: 'root' })
export class PassUploadJobsService {
  private trackRequests = new Subject<number>()
  /** UploadTasksService subscribes here; emit via trackJob(). */
  trackRequests$ = this.trackRequests.asObservable()

  constructor(private API: ApiService) {}

  /** Start tracking a freshly staged job (bell progress + errors dialog when relevant). */
  trackJob(jobId: number) {
    this.trackRequests.next(+jobId)
  }

  getJobs(statuses?: string[], limit?: number): Observable<PassUploadJobsResponse> {
    return this.API.privateQuery<PassUploadJobsResponse>(GET_PASS_UPLOAD_JOBS, () => ({ statuses: statuses ?? null, limit: limit ?? null }))
  }

  getJob(jobId: number): Observable<PassUploadJobResponse> {
    return this.API.privateQuery<PassUploadJobResponse>(GET_PASS_UPLOAD_JOB, () => ({ id: +jobId }))
  }

  getDefects(jobId: number, limit = 50): Observable<PassUploadDefectsResponse> {
    return this.API.privateQuery<PassUploadDefectsResponse>(GET_PASS_UPLOAD_JOB_DEFECTS, () => ({ id: +jobId, limit }))
  }

  /** Acknowledge a finished job so it stops being listed in the bell (server-side, permanent). */
  dismissJob(jobId: number): Observable<PassUploadResponse> {
    return this.API.privateMutation<PassUploadResponse>(DISMISS_PASS_UPLOAD_JOB, () => ({ id: +jobId }))
  }

  /** Take a job off the queue. PAPI refuses once the drain has started it (only queued / awaiting_review cancel). */
  cancelJob(jobId: number): Observable<PassUploadJobResponse> {
    return this.API.privateMutation<PassUploadJobResponse>(CANCEL_PASS_UPLOAD_JOB, () => ({ id: +jobId }))
  }

  getErrorFile(jobId: number): Observable<PassUploadErrorFileResponse> {
    return this.API.privateQuery<PassUploadErrorFileResponse>(GET_PASS_UPLOAD_JOB_ERROR_FILE, () => ({ id: +jobId }))
  }

  /** Convenience: cast a raw job payload from the API into the typed model. */
  asJob(data: RawPassUploadJob | null | undefined): PassUploadJob {
    return Object.assign(new PassUploadJob(), data ?? {})
  }
}
